/* eslint-env browser, es6 */
import m from "mithril";

import {
  BasicWordOrder,
  Grammar,
  Prepositions,
  Position,
  Sentence, RenderUnit
} from "./grammar";
import * as languages from "./languages";
import * as sentences from "./sentences";

function expandWordOrderAcronym(wordOrder: BasicWordOrder): m.Children {
  switch (wordOrder) {
    case BasicWordOrder.SubjectObjectVerb:
      return "Subject–object–verb languages place the object before the verb. "
        + "It is uncommon in European languages, but worldwide it is about as common as SVO. "
        + "Examples include Hindi, Japanese, and Turkish.";
    case BasicWordOrder.SubjectVerbObject:
      return "Subject–verb–object is the typical word order for English and other European languages. "
        + "Cross-linguistically, it is about equally as common as SOV.";
    case BasicWordOrder.VerbSubjectObject:
      return "Verb–subject–object languages place the verb first in the sentence. "
        + "They are considerably rarer than SVO and SOV languages.";
  }
}

interface ControlPanelViewAttrs {
  grammar: Grammar;
}

type VnodeGrammar = m.Vnode<{ grammar: Grammar }>;

interface SettingViewAttrs<T> {
  value: T;
  onUpdateHook: (value: T) => void;
}

class WordOrderSettingView {
  view(vnode: m.Vnode<SettingViewAttrs<BasicWordOrder>>): m.Children {
    const options = [BasicWordOrder.SubjectVerbObject, BasicWordOrder.SubjectObjectVerb, BasicWordOrder.VerbSubjectObject];

    return m("div.word-order",
      options.map((option) => {
        return m("label", [
          m(
            "input",
            {
              "type": "radio",
              name: "word-order",
              value: option,
              checked: vnode.attrs.value === option,
              onchange: (event: InputEvent) => vnode.attrs.onUpdateHook((event.target as any).value),
            }),
          m(TooltipView, {text: option}, expandWordOrderAcronym(option)),
        ])
      })
    );
  }
}

class RadioButtonsView {
  view(vnode: m.Vnode<{
    radioKey: string,
    options: string[],
    tooltips?: (string | null)[],
    value: string,
    onUpdateHook: (value: string) => void
  }>): m.Children {
    const {radioKey, options, tooltips, value, onUpdateHook} = vnode.attrs;

    let r = [];
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      r.push(m("label",
        [
          m("input",
            {
              type: "radio",
              name: radioKey,
              value: option,
              checked: value === option,
              onchange: (event: InputEvent) => onUpdateHook((event.target as any).value),
            }),
          (tooltips && i < tooltips.length && tooltips[i] != null) ? m(TooltipView, {text: option}, tooltips[i]) : option,
        ]));
    }
    return m("div.radio", r);
  }
}

class CheckboxView {
  view(vnode: m.Vnode<SettingViewAttrs<boolean>>): m.Children {
    return m("div.bare-nouns",
      [
        m("label", [
          m("input", {
            type: "checkbox",
            checked: vnode.attrs.value,
            onchange: (event: InputEvent) => vnode.attrs.onUpdateHook((event.target as any).checked)
          }),
          vnode.children,
        ]),
      ]);
  }
}

class LabeledBoxView {
  view(vnode: m.Vnode<{ label: string, big?: boolean, class?: string }>): m.Children {
    return m("div.labeled-box", {class: (vnode.attrs.big ? "big" : "") + " " + (vnode.attrs.class ?? "")}, [
      m("span.labeled-box-label", vnode.attrs.label),
      vnode.children
    ])
  }
}

class TooltipView {
  view(vnode: m.Vnode<{ text: string, underline?: boolean }>): m.Children {
    const underline = vnode.attrs.underline ?? true;
    return m("span.tooltip-box", [
      m("div.tooltip", vnode.children),
      m("span.tooltip-text", {class: underline ? "underline" : ""}, vnode.attrs.text),
    ]);
  }

  // TODO: this fails type-checking when vnode is properly typed as m.VnodeDOM
  oncreate(vnode: any): void {
    const desiredWidth = 350;
    const rect = vnode.dom.getBoundingClientRect();
    const actualWidth = Math.min(desiredWidth, window.innerWidth - rect.left);
    const e = vnode.dom.querySelector(".tooltip");
    if (e) {
      (e as HTMLElement).style.width = `${actualWidth}px`;
    }
  }
}

class ControlPanelViewAttrs {
  grammar: Grammar;
  selected: Grammar | null;
  onUpdateHook: (value: Grammar) => void;
}

class ControlPanelView {
  view(vnode: m.Vnode<ControlPanelViewAttrs>): m.Children {
    const grammar = vnode.attrs.grammar;

    const update = (key: string) => {
      return (value: any) => {
        const clone = structuredClone(grammar);
        // @ts-ignore
        clone[key] = value;
        vnode.attrs.onUpdateHook(clone);
      };
    };

    // return m(LabeledBoxView, {label: "Control panel", big: true, class: "settings"}, [
    return m("div.settings", [
      m("div.row", [
        m(LabeledBoxView, {label: "Basic word order"}, m(WordOrderSettingView, {
          value: grammar.wordOrder,
          onUpdateHook: update("wordOrder")
        })),
        m(LabeledBoxView, {label: "Definiteness"}, [
          m(CheckboxView, {
            value: grammar.indefiniteArticle,
            onUpdateHook: update("indefiniteArticle")
          }, m(TooltipView, {text: "Indefinite article?"}, "The indefinite article in English is the word 'a' or 'an'.")),
          m(CheckboxView, {
            value: grammar.definiteArticle,
            onUpdateHook: update("definiteArticle")
          }, m(TooltipView, {text: "Definite article?"}, "The definite article in English is the word 'the'.")),
        ]),
      ]),
      m("div.row", [
        m(LabeledBoxView, {label: "Nouns"}, [
          m(CheckboxView, {
            value: grammar.postNominalAdjectives,
            onUpdateHook: update("postNominalAdjectives")
          }, m(TooltipView, {text: "Post-nominal adjectives?"}, "Do adjectives precede nouns or follow them?")),
          m(CheckboxView, {
            value: grammar.nominalGender,
            onUpdateHook: update("nominalGender")
          }, m(TooltipView, {text: "Nominal gender?"},
            "Many languages divide nouns into classes that behave differently with respect to adjective–noun agreement "
            + "or other phenomena. In Romance languages, this is typically a two-way distinction between 'masculine' "
            + "and 'feminine' nouns, but in some languages there are more than just 2 classes.")),
          m(CheckboxView, {
            value: grammar.countNouns,
            onUpdateHook: update("countNouns")
          }, m(TooltipView, {text: "Count nouns?"},
            "English has a count vs. mass noun distinction, where some nouns can be directly quantified while others "
            + "cannot be (you can say '3 tables' but not '3 furnitures'). In some languages, all nouns behave like mass "
            + "nouns in English.")),
        ]),
        m(LabeledBoxView, {label: "Inflections"}, [
          m(CheckboxView, {
            value: grammar.nominalInflection,
            onUpdateHook: update("nominalInflection")
          }, m(TooltipView, {text: "Nominal inflections?"},
            "Do nouns take special endings to mark their grammatical function (subject, object, etc.)?")),
          m(CheckboxView, {
            value: grammar.verbConjugation,
            onUpdateHook: update("verbConjugation")
          }, m(TooltipView, {text: "Verb conjugations?"},
            "Do verbs take special endings to mark tense, number, gender, etc.?")),
        ]),
      ]),
      m("div.row", [
        m(LabeledBoxView, {label: "Adpositions?"},
          m(RadioButtonsView, {
            radioKey: "adpositions",
            options: [Prepositions.Prepositions, Prepositions.Postpositions, Prepositions.Both],
            tooltips: [null, "In English, prepositions come before the noun, but in some other languages they come after and are hence known as 'postpositions'. The generic term for a preposition or postpositions is 'adposition'.", null],
            value: grammar.prepositions,
            onUpdateHook: update("prepositions")
          }),
        ),
        m(LabeledBoxView, {label: "Adjunct position?"},
          m(RadioButtonsView, {
            radioKey: "adjunct-position",
            options: [Position.PreVerb, Position.PostVerb],
            value: grammar.adjunctPosition,
            onUpdateHook: update("adjunctPosition")
          }),
        ),
      ]),
      m(PresetsView, {selected: vnode.attrs.selected, onUpdateHook: vnode.attrs.onUpdateHook})
    ]);
  }
}

class SentenceView {
  view(vnode: m.Vnode<{
    units: RenderUnit[],
    spaces: boolean,
    period: string,
    label: string,
    class: string
  }>): m.Children {
    const {units, spaces, label, period} = vnode.attrs;

    const subviews = [];
    let highlightIndex = 0;
    for (let i = 0; i < units.length; i++) {
      const unit = units[i];

      let text = unit.text;
      if (i === 0) {
        text = capitalize(text);
      }

      let class_ = "";
      if (unit.highlight) {
        class_ = `highlight${highlightIndex}`;
        highlightIndex = (highlightIndex + 1) % 2;
      }

      subviews.push(m("span", {class: class_}, text));
      if (spaces && i !== units.length - 1) {
        subviews.push(" ");
      }
    }
    subviews.push(period);

    return m("div", {class: vnode.attrs.class}, [
      m("div.label", `${label}:`),
      m("div.content", subviews)]);
  }
}

class TranslationView {
  view(vnode: m.Vnode<{ sentence: Sentence, grammar: Grammar }>): m.Children {
    const {sentence, grammar} = vnode.attrs;

    let translation;
    let translit;
    if (languages.isEqual(grammar, languages.RUSSIAN)) {
      translation = sentence.translations.russian;
      translit = sentence.translations.russianTranslit;
    } else if (languages.isEqual(grammar, languages.FRENCH)) {
      translation = sentence.translations.french;
    } else if (languages.isEqual(grammar, languages.CHINESE)) {
      translation = sentence.translations.chinese;
      translit = sentence.translations.chineseTranslit;
    } else if (languages.isEqual(grammar, languages.HINDI)) {
      translation = sentence.translations.hindi;
      translit = sentence.translations.hindiTranslit;
    }

    const empty = {
      units: [{text: "-", highlight: false}],
      spaces: false,
      period: "",
    };

    if (!translation) {
      translation = empty;
    }

    if (!translit) {
      translit = empty;
    }

    return [
      m(SentenceView, {...translation, label: "Translation", class: "translation"}),
      m(SentenceView, {...translit, label: "Transliteration", class: "transliteration"}),
    ];
  }
}

class DebugView {
  view(vnode: VnodeGrammar): m.Children {
    return m("pre", JSON.stringify(vnode.attrs.grammar, null, 2));
  }
}

class HeaderView {
  view(): m.Children {
    return [
      m("h1", [m("span.poly", "Poly"), m("span.grammar", "grammar"), m("span.ous", "ous")]),
      m("div.subtitle",
        [
          m("span", "Explore linguistic diversity"),
          m("a", {href: "/blog/2024/06/polygrammarous"}, "about")]),
      m("hr"),
    ];
  }
}

class PresetsView {
  view(vnode: m.Vnode<{ selected: Grammar | null, onUpdateHook: (value: Grammar) => void }>): m.Children {
    const {selected, onUpdateHook} = vnode.attrs;
    return m("div.buttons",
      languages.ALL.map(lang => m(LanguageButtonView, {language: lang, selected, onUpdateHook})));
  }
}

class LanguageButtonView {
  view(vnode: m.Vnode<{
    language: Grammar,
    selected: Grammar | null,
    onUpdateHook: (value: Grammar) => void
  }>): m.Children {
    const {language, selected, onUpdateHook} = vnode.attrs;
    return m("button", {
      class: (selected && languages.isEqual(language, selected)) ? "selected" : "",
      onclick: () => onUpdateHook(language)
    }, m(TooltipView, {text: language.name, underline: false}, language.description));
  }
}

class PageView {
  private sentence: Sentence;
  private grammar: Grammar;

  constructor(_vnode: m.Vnode) {
    this.sentence = sentences.CHASE;
    this.grammar = languages.DEFAULT;
  }

  view(): m.Children {
    const onUpdate = (newGrammar: Grammar) => {
      this.grammar = newGrammar;
      m.redraw();
    };

    const languageSelected = getLanguageSelected(this.grammar);
    const units = this.sentence.materialize(this.grammar);

    return [
      m(HeaderView),
      m(SentenceView, {units, spaces: true, period: ".", label: "English", class: "sentence"}),
      m(TranslationView, {sentence: this.sentence, grammar: this.grammar}),
      m(ControlPanelView, {grammar: this.grammar, selected: languageSelected, onUpdateHook: onUpdate}),
      inDebugMode() ? m(DebugView, {grammar: this.grammar}) : null,
    ];
  }
}

function getLanguageSelected(grammar: Grammar): Grammar | null {
  for (const lang of languages.ALL) {
    if (languages.isEqual(grammar, lang)) {
      return lang;
    }
  }

  return null;
}

function capitalize(sentence: string): string {
  return sentence[0].toUpperCase() + sentence.slice(1);
}

function inDebugMode() {
  const url = new URL(window.location.href);
  if (url.searchParams.has("no-debug")) {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname.endsWith(".localhost") || hostname.endsWith(".local") || hostname.endsWith(".test");
}

function main() {
  const root = document.querySelector(".mithril-mount");
  if (root === null) {
    throw "Mithril.js mount-point not found in document.";
  }
  m.mount(root, PageView);
}

main();
